import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTrial } from '../context/TrialContext';
import {
  createPaymentLink,
  formatPrice,
  reconcileMercadoPagoSubscriptionReturn,
} from '../utils/astropayService';
import toast from 'react-hot-toast';
import {
  Clock,
  ArrowRight,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Zap,
  ExternalLink,
  ShieldAlert,
} from 'lucide-react';
import GastifyBrand from '../components/public/GastifyBrand';

export default function SuscripcionStatus() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    loadingTrial,
    organization,
    currentPlanKey,
    currentPlan,
    isTrialing,
    isActive,
    isGrace,
    daysLeft,
    graceDaysLeft,
    subscriptionExpiresAt,
    refreshTrial,
    billingProviderLabel,
  } = useTrial();

  const [generating, setGenerating] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [redirected, setRedirected] = useState(false);
  const [reconcilingReturn, setReconcilingReturn] = useState(false);

  // Vigente = trial, active, or grace
  const isVigente = isTrialing || isActive || isGrace;
  const visibleExpiryDate = isTrialing
    ? (organization?.trial_ends_at ? new Date(organization.trial_ends_at) : null)
    : subscriptionExpiresAt;

  let remainingDays = 0;
  if (isTrialing) {
    remainingDays = daysLeft;
  } else if (isGrace) {
    remainingDays = graceDaysLeft;
  } else if (isActive && subscriptionExpiresAt) {
    const ms = subscriptionExpiresAt.getTime() - new Date().getTime();
    remainingDays = Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  } else if (isActive && !subscriptionExpiresAt) {
    remainingDays = 30; // fallback if infinite active
  }

  const isWarning = remainingDays <= 5;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const provider = String(params.get('provider') || '').toLowerCase();
    const paymentId = params.get('payment_id') || params.get('collection_id') || '';
    const externalReference = params.get('external_reference') || '';
    const paymentStatus =
      params.get('collection_status')
      || params.get('status')
      || '';

    if (!organization?.id) return;
    if (provider !== 'mercadopago' && !paymentId && !externalReference) return;

    let cancelled = false;

    const reconcileReturn = async () => {
      setReconcilingReturn(true);
      try {
        await reconcileMercadoPagoSubscriptionReturn({
          payment_id: paymentId,
          collection_id: params.get('collection_id'),
          collection_status: params.get('collection_status'),
          status: params.get('status'),
          external_reference: externalReference,
          preference_id: params.get('preference_id'),
        });

        if (!cancelled) {
          await refreshTrial();
          if (paymentStatus.toLowerCase() === 'approved') {
            toast.success('Pago confirmado. Estamos activando tu suscripcion.');
          }
          navigate('/sistema/status', { replace: true });
        }
      } catch (err) {
        console.error('[SuscripcionStatus] reconcile return error:', err);
        if (!cancelled) {
          toast.error(err?.message || 'No pudimos confirmar el pago automaticamente.');
        }
      } finally {
        if (!cancelled) setReconcilingReturn(false);
      }
    };

    void reconcileReturn();

    return () => {
      cancelled = true;
    };
  }, [location.search, navigate, organization?.id, refreshTrial]);

  useEffect(() => {
    if (loadingTrial || reconcilingReturn) return;

    if (isVigente && !redirected) {
      if (countdown > 0) {
        const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
        return () => clearTimeout(timer);
      } else {
        setRedirected(true);
        sessionStorage.setItem('hasSeenStatus', 'true');
        navigate('/sistema', { replace: true });
      }
    }
  }, [loadingTrial, reconcilingReturn, isVigente, countdown, navigate, redirected]);

  const handlePay = async () => {
    if (!organization?.id || !currentPlan) {
      toast.error('No se pudo determinar tu plan.');
      return;
    }
    setGenerating(true);
    try {
      const result = await createPaymentLink({
        orgId: organization.id,
        planKey: currentPlan.key || currentPlanKey,
        planName: currentPlan.name,
        amount: currentPlan.price_monthly,
        currency: currentPlan.currency || 'ARS',
      });

      await refreshTrial();
      
      // Marcar como visto porque al volver del proveedor ya podra operar (o ver pending)
      sessionStorage.setItem('hasSeenStatus', 'true');

      if (result.payment_url) {
        window.location.href = result.payment_url;
      } else {
        toast.error(
          `No pudimos generar el link de pago con ${billingProviderLabel}. Probá de nuevo en unos minutos o escribinos por WhatsApp.`,
          { duration: 7000 },
        );
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Error al generar el link de pago.');
    } finally {
      setGenerating(false);
    }
  };

  const handleReadOnly = () => {
    sessionStorage.setItem('hasSeenStatus', 'true');
    navigate('/sistema', { replace: true });
  };

  if (loadingTrial || reconcilingReturn) {
    return (
      <div className="min-h-screen bg-[var(--gst-cream-50)] flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="animate-spin text-[var(--gst-mint-500)]" size={40} />
          {reconcilingReturn && (
            <p className="text-[12px] font-bold uppercase tracking-widest text-[var(--gst-graphite-500)]">
              Confirmando pago...
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--gst-cream-50)] flex flex-col items-center justify-center p-4" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700;9..144,800&family=Manrope:wght@400;500;600;700;800&display=swap');
        .gst-display { font-family: 'Fraunces', Georgia, serif; }
      `}</style>
      
      <div className="mb-8 scale-110">
        <GastifyBrand textTone="dark" iconSize={50} subtitle="Estado de cuenta" />
      </div>

      <div className="w-full max-w-md bg-[var(--surface)] border border-[var(--gst-border-strong)] rounded-2xl p-8 shadow-xl text-center relative overflow-hidden">
        {/* Decorativo top */}
        <div className={`absolute top-0 left-0 right-0 h-1.5 ${isVigente ? (isWarning ? 'bg-orange-400' : 'bg-[var(--gst-mint-500)]') : 'bg-red-500'}`} />

        {isVigente ? (
          <div className="animate-in fade-in zoom-in-95 duration-500">
            <div className="flex justify-center mb-6">
              {isWarning ? (
                <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center border-4 border-orange-100">
                  <AlertTriangle size={36} className="text-orange-500" />
                </div>
              ) : (
                <div className="w-20 h-20 bg-[var(--gst-mint-50)] rounded-full flex items-center justify-center border-4 border-[var(--gst-mint-100)]">
                  <CheckCircle2 size={40} className="text-[var(--gst-mint-600)]" />
                </div>
              )}
            </div>

            <h2 className="gst-display text-3xl font-black text-[var(--gst-graphite-900)] mb-2">
              Suscripción Vigente
            </h2>
            <p className="text-[15px] text-[var(--gst-graphite-600)] font-medium mb-8">
              Tu acceso está habilitado y funcionando correctamente.
            </p>

            <div className="inline-flex items-center justify-center gap-3 bg-[var(--gst-cream-50)] border border-[var(--gst-border)] rounded-full px-5 py-2.5 mb-8">
              <Clock size={16} className={isWarning ? 'text-orange-500' : 'text-[var(--gst-graphite-500)]'} />
              <span className="text-[13px] font-bold text-[var(--gst-graphite-800)] uppercase tracking-widest">
                {visibleExpiryDate ? (
                  <>
                    Vence{' '}
                    <span className={`text-[15px] font-black ${isWarning ? 'text-red-500' : 'text-[var(--gst-mint-600)]'}`}>
                      {visibleExpiryDate.toLocaleDateString('es-AR')}
                    </span>
                  </>
                ) : (
                  'Acceso vigente'
                )}
              </span>
            </div>

            <div className="flex flex-col items-center gap-2 text-[var(--gst-graphite-400)]">
              <Loader2 size={20} className="animate-spin" />
              <p className="text-xs font-bold uppercase tracking-widest">
                Redirigiendo en {countdown}...
              </p>
            </div>
          </div>
        ) : (
          <div className="animate-in fade-in zoom-in-95 duration-500">
             <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center border-4 border-red-100">
                <ShieldAlert size={36} className="text-red-500" />
              </div>
            </div>

            <h2 className="gst-display text-3xl font-black text-[var(--gst-graphite-900)] mb-2">
              Suscripción Vencida
            </h2>
            <p className="text-[14px] text-[var(--gst-graphite-600)] font-medium mb-6 px-2">
              Para seguir operando, facturando e ingresando información, necesitás renovar tu suscripción mensual.
            </p>

            {currentPlan && (
               <div className="bg-[var(--gst-cream-50)] border border-[var(--gst-border-strong)] rounded-xl p-4 mb-6 text-left flex justify-between items-center">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-[var(--gst-graphite-500)] mb-1">
                      Plan Actual
                    </div>
                    <div className="font-bold text-[14px] text-[var(--gst-graphite-900)]">
                      {currentPlan.name}
                    </div>
                  </div>
                  <div className="text-right">
                     <div className="text-[16px] font-black text-[var(--gst-graphite-900)]">
                        {formatPrice(currentPlan.price_monthly)}
                     </div>
                     <div className="text-[10px] font-bold text-[var(--gst-graphite-500)]">/mes</div>
                  </div>
               </div>
            )}

            <div className="space-y-3">
              <button
                onClick={handlePay}
                disabled={generating}
                className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl bg-[var(--gst-mint-500)] text-white font-black text-[15px] hover:bg-[var(--gst-mint-600)] transition-all shadow-[0_8px_20px_rgba(16,185,129,0.2)] disabled:opacity-50"
              >
                {generating ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Procesando pago...
                  </>
                ) : (
                  <>
                    <Zap size={18} />
                    {`Pagar mi plan con ${billingProviderLabel}`}
                    <ExternalLink size={14} />
                  </>
                )}
              </button>

              <button
                onClick={handleReadOnly}
                className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl border border-[var(--gst-border-strong)] bg-transparent text-[var(--gst-graphite-700)] font-bold text-[13px] hover:bg-[var(--surface-hover)] transition-all"
              >
                Ver mis datos sin operar <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
